import React, { useState, useCallback, useEffect, useRef, useContext } from 'react';
import './DataDiagramField.less';
import classNames from 'classnames';
import { IDiagramTreeNode } from './DataDiagram';
import { TextField } from 'components/inputs/TextField';
import { HasOpenState } from 'hooks/HasOpenState';
import { DataDesignerContext } from './DataDesignerContext';
import { ITreeNodeContentProps, TreeNodeContent } from 'components/Tree/CommonTree/CommonTreeNode';

interface IDataDiagramFieldProps {
  readonly node: React.PropsWithChildren<ITreeNodeContentProps<IDiagramTreeNode>>;
  onNameChanged?: (name: string) => void;
}

export const DataDiagramField = React.memo(({ node, onNameChanged }: IDataDiagramFieldProps) => {
  const { dataDescription, isRoot } = node.data!;
  const { isOpen: isInputVisible, setOpen, setClosed } = HasOpenState();
  const [name, setName] = useState(dataDescription.Name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { noDiagramNameMessage } = useContext(DataDesignerContext);

  useEffect(() => {
    if (isInputVisible && inputRef.current != null) {
      inputRef.current.focus();
    }
  }, [isInputVisible]);

  useEffect(() => {
    setName(dataDescription.Name);
  }, [dataDescription.Name]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleInputClosed = useCallback(() => {
    setClosed();
    onNameChanged?.(name);
  }, [name, onNameChanged, setClosed]);

  const handleInputKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleInputClosed();
      }
    },
    [handleInputClosed]
  );

  const handleChevronMouseEvent: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      ((event.target as HTMLElement)?.offsetParent as HTMLElement)?.focus();
      event.stopPropagation();
      node.onExpandNodeClick?.(event);
    },
    [node]
  );

  //to prevent dragging drop when selecting text in input
  const handleInputMouseEvent = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <TreeNodeContent
      {...node}
      name={!dataDescription.DisplayName ? noDiagramNameMessage : dataDescription.DisplayName}
      onDoubleClick={isRoot ? setOpen : undefined}
      onExpandNodeClick={handleChevronMouseEvent}
      className={classNames('dpc-diagram-tree-node__content', {
        'dpc-diagram-tree-node__content--editing': isInputVisible,
        'dpc-diagram-tree-node__content--complex': dataDescription.HasReadableSubElements,
      })}
      components={{
        title: isInputVisible ? (
          <TextField
            ref={inputRef}
            className="dpc-diagram-tree-node__inline-editor"
            placeholder={noDiagramNameMessage}
            value={name}
            onChange={handleInputChange}
            onKeyUp={handleInputKeyUp}
            onMouseDown={handleInputMouseEvent}
            onMouseUp={handleInputMouseEvent}
            onBlur={handleInputClosed}
          />
        ) : (
          undefined
        ),
      }}
    />
  );
});
